
import { Phone, Mail01 as Mail, MessageSquare01 as MessageSquare, Eye, AlertTriangle, CheckCircle, CalendarDate as CalendarDays } from "@untitledui/icons"
import { Link } from "react-router"
import type { ApiEstimate } from "@/lib/api/types"
import { vehicleDisplayName, estimateCustomerName, estimateCustomerEmail, estimateCustomerPhone } from "@/lib/api/types"
import { estimateAuthorShort, isDeferred } from "@/lib/api/estimates"
import { estimateVerdict } from "@/lib/estimates/estimate-verdict"
import { readIntakeMeta } from "@/lib/api/public-consumer"
import { SURFACE_LABEL } from "@/lib/intake/intake-config"

interface PipelineCardProps {
  estimate: ApiEstimate
  isNew?: boolean
  isConsumerLead?: boolean
  isTotalLoss?: boolean
  onViewDetails?: (estimateId: string) => void
  onCall?: (phone: string) => void
  onEmail?: (email: string) => void
  onSMS?: (estimateId: string) => void
  onSchedule?: (estimateId: string) => void
  onConfirm?: (estimateId: string) => void
  isBodyShop?: boolean
}

export function PipelineCard({ estimate, isNew, isConsumerLead, isTotalLoss, onViewDetails, onCall, onEmail, onSMS, onConfirm }: PipelineCardProps) {
  const customerName = estimateCustomerName(estimate)
  const customerEmail = estimateCustomerEmail(estimate)
  const customerPhone = estimateCustomerPhone(estimate)
  const vehicleName = vehicleDisplayName(estimate.vehicle)
  const author = estimateAuthorShort(estimate)
  const deferred = isDeferred(estimate)
  // Consumer-intake provenance: which surface the lead came in on, and
  // whether the shop is still waiting on photos (contact-only lead).
  const intake = isConsumerLead ? readIntakeMeta(estimate) : null
  const awaitingPhotos = intake != null && (estimate.images?.length ?? 0) === 0
  const leadChip = intake
    ? `LEAD · ${SURFACE_LABEL[intake.surface]}${awaitingPhotos ? " · awaiting photos" : ""}`
    : "LEAD"

  return (
    <div
      className={`rounded-xl border border-zinc-200/60 bg-white hover:shadow-md transition-all cursor-pointer group ${isTotalLoss ? 'border-l-4 border-l-red-500' : ''}`}
      onClick={() => onViewDetails?.(String(estimate.id))}
    >
      <div className="p-3 space-y-2.5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="size-7 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
              <span className="text-[10px] font-semibold text-zinc-600">
                {customerName.split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
              </span>
            </div>
            <h4 className="font-semibold text-sm text-zinc-900 line-clamp-1 flex-1">{customerName}</h4>
          </div>
          <div className="flex gap-1 shrink-0">
            {deferred && (
              <span className="bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 rounded-md">
                DEFERRED
              </span>
            )}
            {isTotalLoss && (
              <span className="inline-flex items-center gap-0.5 bg-red-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-md">
                <AlertTriangle className="size-3" />
                TL
              </span>
            )}
            {/* Quiet chips — solid fills are reserved for total-loss (the one
                signal that must shout). No pulsing; a board full of blinking
                cards reads as a carnival, not a work queue. */}
            {isConsumerLead && (
              <span className="bg-zinc-100 text-zinc-600 ring-1 ring-inset ring-zinc-200 text-[10px] font-medium px-1.5 py-0.5 rounded-md whitespace-nowrap">
                {leadChip}
              </span>
            )}
            {isNew && (
              <span className="bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
                NEW
              </span>
            )}
          </div>
        </div>

        {/* Vehicle + originator */}
        <p className="text-xs text-zinc-400 line-clamp-1">
          {vehicleName}
          {author && <span> · by {author}</span>}
        </p>

        {/* Amount */}
        <div className="pt-2 border-t border-zinc-100">
          <div className="flex items-baseline gap-1">
            <p className="text-lg font-bold text-zinc-900 font-mono">${estimate.avg.toLocaleString()}</p>
            {estimate.min > 0 && estimate.max > 0 && estimate.min !== estimate.max && (
              <p className="text-xs text-zinc-400 font-mono">(${estimate.min.toLocaleString()} - ${estimate.max.toLocaleString()})</p>
            )}
          </div>
        </div>

        {/* Confirm-assist — the verdict IS the action (Pending only). Clean
            cards get a one-tap Confirm; the teal button itself says "looks
            good", so no green pill doubles it. Flagged cards show the ⚠ reason
            and their button opens the estimate for review — the doctrine:
            batch-clear the clean, open the flagged few. */}
        {estimate.status === "Pending" && (() => {
          const verdict = estimateVerdict(estimate)
          const review = verdict.level === "review"
          return (
            <div className="pt-1 space-y-1.5">
              {review && (
                <span
                  title={verdict.reasons.join(" · ")}
                  className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded-md px-1.5 py-0.5 max-w-full"
                >
                  <AlertTriangle className="size-3 shrink-0" aria-hidden />
                  <span className="truncate">{verdict.reasons[0]}</span>
                </span>
              )}
              {review ? (
                <button
                  type="button"
                  aria-label={`Review estimate for ${customerName}`}
                  className="w-full h-11 rounded-lg bg-amber-500 text-white text-[13px] font-semibold hover:bg-amber-600 transition-colors inline-flex items-center justify-center gap-1.5"
                  onClick={(e) => { e.stopPropagation(); onViewDetails?.(String(estimate.id)) }}
                >
                  <AlertTriangle className="size-4" aria-hidden /> Review
                </button>
              ) : (
                onConfirm && (
                  <button
                    type="button"
                    aria-label={`Confirm estimate for ${customerName}`}
                    className="w-full h-11 rounded-lg bg-teal-600 text-white text-[13px] font-semibold hover:bg-teal-700 transition-colors inline-flex items-center justify-center gap-1.5"
                    onClick={(e) => { e.stopPropagation(); onConfirm(String(estimate.id)) }}
                  >
                    <CheckCircle className="size-4" aria-hidden /> Confirm
                  </button>
                )
              )}
            </div>
          )
        })()}

        {/* Confirmed = reviewed, nobody contacted. The call is a TASK on /tasks;
            this is just the door to it — the board is where estimates live,
            not where a person works a phone. */}
        {estimate.status === "Confirmed" && (
          <Link
            to={`/tasks?estimate=${estimate.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-teal-700 hover:text-teal-800 transition-colors pt-1"
          >
            <Phone className="size-3.5" aria-hidden /> Task: reach out →
          </Link>
        )}

        {/* Scheduled */}
        {estimate.scheduledAt && (
          <div className="text-xs flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-zinc-100 text-zinc-700">
            <CalendarDays className="size-3" />
            <span className="font-medium">
              {new Date(estimate.scheduledAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
          </div>
        )}

        {/* Quick Contact Actions — visible by default so they work on touch
            (the service lane's device), brightening on hover/focus for desktop
            polish. Was hover-only, i.e. dead on touch (F12). */}
        <div className="flex gap-1 opacity-70 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 pt-1">
          {onCall && (
            <button
              className="size-9 rounded-lg flex items-center justify-center hover:bg-zinc-100 transition-colors duration-100"
              onClick={(e) => { e.stopPropagation(); onCall(customerPhone || "") }}
              title="Call Customer"
            >
              <Phone className="size-3.5 text-zinc-500" />
            </button>
          )}
          {onEmail && (
            <button
              className="size-9 rounded-lg flex items-center justify-center hover:bg-zinc-100 transition-colors duration-100"
              onClick={(e) => { e.stopPropagation(); onEmail(customerEmail || "") }}
              title="Email Customer"
            >
              <Mail className="size-3.5 text-zinc-500" />
            </button>
          )}
          {onSMS && (
            <button
              className="size-9 rounded-lg flex items-center justify-center hover:bg-zinc-100 transition-colors duration-100"
              onClick={(e) => { e.stopPropagation(); onSMS(String(estimate.id)) }}
              title="Text Customer"
            >
              <MessageSquare className="size-3.5 text-zinc-500" />
            </button>
          )}
          <button
            className="size-8 rounded-lg flex items-center justify-center hover:bg-zinc-100 transition-colors duration-100 ml-auto"
            onClick={(e) => { e.stopPropagation(); onViewDetails?.(String(estimate.id)) }}
            title="View Details"
          >
            <Eye className="size-3.5 text-zinc-500" />
          </button>
        </div>
      </div>
    </div>
  )
}
